// Derived VALD metrics and the flag rule engine (brief tasks 4 & 5).
// Everything fails soft: a missing input yields null rather than throwing, so a
// partially-synced test never breaks the page.

import type { TestingResult } from '@/hooks/useTestingResults'

export type FlagStatus = 'green' | 'amber' | 'red' | 'none'
export interface Flag {
  status: FlagStatus
  reason: string
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** Signed limb asymmetry: ((L − R) / max(L,R)) × 100. Positive = left dominant. */
export function signedAsymmetry(left: unknown, right: unknown): number | null {
  const l = num(left)
  const r = num(right)
  if (l == null || r == null) return null
  const peak = Math.max(l, r)
  if (peak === 0) return null
  return ((l - r) / peak) * 100
}

/** "9.9% L" — absolute magnitude plus the dominant side. */
export function formatAsymmetry(signed: number | null): string {
  if (signed == null) return '—'
  const mag = Math.abs(Math.round(signed * 10) / 10)
  if (mag === 0) return '0%'
  return `${mag}% ${signed > 0 ? 'L' : 'R'}`
}

/**
 * Asymmetry for a row: prefer a synced value, else compute from limb values.
 * VALD doesn't return asymmetry for single-leg CMJ, so this is what makes that
 * tile render instead of an em dash.
 */
export function rowAsymmetry(r: TestingResult | undefined): number | null {
  if (!r) return null
  const computed = signedAsymmetry(r.values?.left, r.values?.right)
  if (computed != null) return computed
  const synced = num(r.values?.asymmetry)
  return synced
}

/** Count of populated metric values — used to pick the best of same-day repeats. */
export function completeness(r: TestingResult): number {
  return Object.values(r.values ?? {}).filter((v) => v != null).length
}

/** The metric that must be present for a row to be worth showing at all. */
export const PRIMARY_FIELD: Record<string, string> = {
  cmj: 'height',
  sl_cmj: 'left',
  sl_hop: 'left',
  sj: 'height',
  dj: 'height',
  imtp: 'peak_force',
  rsi: 'rsi',
  hip_abd: 'left',
  hip_add: 'left',
}

/**
 * One row per (test, date): drops same-day repeats keeping the most complete,
 * and suppresses rows whose primary metric is missing. Newest first.
 */
export function bestPerSession(rows: TestingResult[]): TestingResult[] {
  const byDate = new Map<string, TestingResult>()
  for (const r of rows) {
    const primary = PRIMARY_FIELD[r.test_id]
    if (primary && num(r.values?.[primary]) == null) continue // all-dash row
    const key = `${r.test_id}|${r.date}`
    const existing = byDate.get(key)
    if (!existing || completeness(r) > completeness(existing)) byDate.set(key, r)
  }
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : -1))
}

/** Dynamic Strength Index = CMJ peak force ÷ IMTP peak force. */
export function dsi(cmjPeak: unknown, imtpPeak: unknown): number | null {
  const c = num(cmjPeak)
  const i = num(imtpPeak)
  if (c == null || i == null || i === 0) return null
  return c / i
}

const daysBetween = (a: string, b: string) =>
  Math.abs((new Date(a).getTime() - new Date(b).getTime()) / 86400000)

/**
 * Nearest CMJ/IMTP pair within `maxDays`. DSI is only meaningful when the two
 * tests are close together, so an out-of-window pair returns null.
 */
export function findDsiPair(rows: TestingResult[], maxDays = 14) {
  const cmj = rows.filter((r) => r.test_id === 'cmj')
  const imtp = rows.filter((r) => r.test_id === 'imtp')
  let best: { cmj: TestingResult; imtp: TestingResult; gap: number } | null = null
  for (const c of cmj) {
    for (const i of imtp) {
      const gap = daysBetween(c.date, i.date)
      if (gap > maxDays) continue
      if (!best || c.date > best.cmj.date || (c.date === best.cmj.date && gap < best.gap)) {
        best = { cmj: c, imtp: i, gap }
      }
    }
  }
  if (!best) return null
  const value = dsi(best.cmj.values?.peak_force, best.imtp.values?.peak_force)
  return value == null ? null : { value, gap: best.gap, date: best.cmj.date }
}

/** Modified RSI = jump height (m) ÷ time to takeoff (s). */
export function modifiedRsi(heightCm: unknown, timeToTakeoffMs: unknown): number | null {
  const h = num(heightCm)
  const t = num(timeToTakeoffMs)
  if (h == null || t == null || t === 0) return null
  return h / 100 / (t / 1000)
}

/** Fallback body mass: IMTP peak force (N) ÷ relative force (N/kg). */
export function estimatedBodyMass(peakForce: unknown, relativeForce: unknown): number | null {
  const p = num(peakForce)
  const r = num(relativeForce)
  if (p == null || r == null || r === 0) return null
  return p / r
}

/** Adduction ÷ abduction peak force. Below 0.90 flags an adductor deficit. */
export function addAbdRatio(add: unknown, abd: unknown): number | null {
  const a = num(add)
  const b = num(abd)
  if (a == null || b == null || b === 0) return null
  return a / b
}

export const CV_MIN_TRIALS = 6
export const CV_NOISY_THRESHOLD = 10

/**
 * Coefficient of variation for one metric across all stored trials. Needs at
 * least CV_MIN_TRIALS readings — below that it's noise, not a measurement.
 */
export function coefficientOfVariation(values: (number | null | undefined)[]): number | null {
  const xs = values.map(num).filter((v): v is number => v != null)
  if (xs.length < CV_MIN_TRIALS) return null
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length
  if (mean === 0) return null
  const variance = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / (xs.length - 1)
  return (Math.sqrt(variance) / mean) * 100
}

/** Percentage change from the athlete's first recorded value. */
export function changeFromBaseline(current: unknown, first: unknown): number | null {
  const c = num(current)
  const f = num(first)
  if (c == null || f == null || f === 0) return null
  return ((c - f) / f) * 100
}

export const STALE_DAYS = 90
export const ASYM_RED = 20
export const ASYM_AMBER = 15

export function daysSince(date: string): number {
  return Math.floor((Date.now() - new Date(date).getTime()) / 86400000)
}

const SEVERITY: Record<FlagStatus, number> = { none: 0, green: 1, amber: 2, red: 3 }

export interface FlagInput {
  /** Best-per-session rows, newest first. */
  rows: TestingResult[]
}

/**
 * Evaluate an athlete's most recent test set. Returns the worst applicable flag
 * plus every note, so the UI can show the headline and the detail.
 */
export function evaluateFlags({ rows }: FlagInput): {
  flag: Flag
  notes: string[]
  dsiValue: number | null
  stale: boolean
  lastTestDate: string | null
} {
  const notes: string[] = []
  let flag: Flag = { status: 'none', reason: 'Not enough data to assess' }
  const raise = (status: FlagStatus, reason: string) => {
    if (SEVERITY[status] >= SEVERITY[flag.status]) flag = { status, reason }
  }

  const lastTestDate = rows.length ? rows[0].date : null
  const stale = lastTestDate != null && daysSince(lastTestDate) > STALE_DAYS
  if (!rows.length) return { flag, notes, dsiValue: null, stale: false, lastTestDate: null }
  if (stale) {
    notes.push(`Last tested ${daysSince(lastTestDate!)} days ago — results may not reflect current form.`)
  }

  // Asymmetry, suppressed where that metric is too noisy for this athlete.
  for (const testId of ['sl_cmj', 'sl_hop', 'imtp', 'hip_abd', 'hip_add']) {
    const latest = rows.find((r) => r.test_id === testId)
    if (!latest) continue
    const asym = rowAsymmetry(latest)
    if (asym == null) continue
    const cv = coefficientOfVariation(
      rows.filter((r) => r.test_id === testId).map((r) => rowAsymmetry(r))
    )
    if (cv != null && cv > CV_NOISY_THRESHOLD) {
      notes.push(`${testId.toUpperCase()}: measurement variability high, retest before interpreting.`)
      continue
    }
    const mag = Math.abs(asym)
    if (mag >= ASYM_RED) raise('red', 'Investigate limb difference')
    else if (mag >= ASYM_AMBER) raise('amber', 'Monitor limb difference')
  }

  // Dynamic strength index — only when the source tests are close together.
  const pair = findDsiPair(rows)
  const dsiValue = pair?.value ?? null
  if (dsiValue != null) {
    if (dsiValue > 0.8) raise('amber', 'Prioritise maximal strength development')
    else if (dsiValue < 0.6) raise('amber', 'Prioritise ballistic and velocity work')
    else raise('green', 'Balanced strength and power profile')
  }

  // Adductor deficit.
  const abd = rows.find((r) => r.test_id === 'hip_abd')
  const add = rows.find((r) => r.test_id === 'hip_add')
  if (abd && add) {
    for (const side of ['left', 'right'] as const) {
      const ratio = addAbdRatio(add.values?.[side], abd.values?.[side])
      if (ratio != null && ratio < 0.9) raise('amber', 'Adductor strength deficit')
    }
  }

  // Fatigue: a drop beyond this athlete's own noise floor vs their recent average.
  const cmjRows = rows.filter((r) => r.test_id === 'cmj')
  if (cmjRows.length >= 5) {
    const heights = cmjRows.map((r) => num(r.values?.height))
    const cv = coefficientOfVariation(heights)
    const current = heights[0]
    const recent = heights.slice(1, 5).filter((v): v is number => v != null)
    if (cv != null && current != null && recent.length === 4) {
      const avg = recent.reduce((a, b) => a + b, 0) / recent.length
      const dropPct = ((avg - current) / avg) * 100
      if (dropPct > 1.5 * cv) raise('amber', 'Possible accumulated fatigue')
    }
  }

  if (stale) flag = { status: 'none', reason: `Stale — last tested ${daysSince(lastTestDate!)} days ago` }
  return { flag, notes, dsiValue, stale, lastTestDate }
}

/** Athletes are due for retesting after this long without a test (12 weeks). */
export const TESTING_DUE_DAYS = 84

export interface TestingDue {
  due: boolean
  neverTested: boolean
  days: number | null
  label: string
}

/** Is this athlete due for testing? `lastDate` may be null (never tested). */
export function testingDue(lastDate: string | null | undefined): TestingDue {
  if (!lastDate) {
    return { due: true, neverTested: true, days: null, label: 'Never tested' }
  }
  const days = daysSince(lastDate)
  return {
    due: days >= TESTING_DUE_DAYS,
    neverTested: false,
    days,
    label: `Last tested ${days} days ago`,
  }
}

export type ChangeVerdict = 'improved' | 'steady' | 'declined'

export interface ChangeReading {
  verdict: ChangeVerdict
  /** Parent-facing sentence. Never speculates about causes. */
  note: string
}

/**
 * Interpret a change honestly for a parent audience.
 *
 * The only cause this can legitimately assert is measurement noise: if a change
 * is smaller than the athlete's own test-to-test variation, it is not evidence
 * of anything. Beyond that it describes the change plainly and leaves the
 * explanation to the coach — the app cannot know whether a drop reflects a
 * training focus, a growth spurt, illness or fatigue.
 */
export function readChange(
  pctChange: number | null,
  cv: number | null,
  lowerIsBetter = false
): ChangeReading {
  if (pctChange == null) {
    return { verdict: 'steady', note: 'Not enough tests yet to show a trend.' }
  }
  const improved = lowerIsBetter ? pctChange < 0 : pctChange > 0
  const magnitude = Math.abs(pctChange)

  // Within their own noise floor → genuinely not a change worth reading into.
  const noiseFloor = cv != null ? Math.max(cv, 3) : 5
  if (magnitude < noiseFloor) {
    return {
      verdict: 'steady',
      note: 'Holding steady — this sits inside normal test-to-test variation.',
    }
  }
  if (improved) return { verdict: 'improved', note: 'Clear improvement on their previous result.' }
  return {
    verdict: 'declined',
    note:
      'Down on their previous result. Scores naturally rise and fall depending on what the ' +
      'training block has been developing, and on how rested an athlete is on test day.',
  }
}
